import { HttpClient } from '@angular/common/http';
import { RequestTypeEnum } from '../../utils/enums';
import { sanitizeHtml } from 'src/app/core/common';

export const REQUEST_TYPE_HEADER_KEY = 'REQUEST_TYPE_HEADER';

export class BaseApiService {
    constructor(public http: HttpClient) {
    }

    private prepareOptions(options?: any, requestType?: RequestTypeEnum): any {
        if (!options) {
            options = {};
        }

        options.withCredentials = true;

        if (!requestType) {
            return options;
        }

        const internalOptions = { ...options };
        internalOptions.headers = internalOptions.headers || {};
        internalOptions.headers[REQUEST_TYPE_HEADER_KEY] = requestType;
        return internalOptions;
    }

    protected get<T>(url: string, options?: any, requestType?: RequestTypeEnum): Promise<any> {
        return (this.http.get<T>(url, this.prepareOptions(options, requestType)).toPromise());
    }

    protected post<T>(url: string, data?: any, options?: any, requestType?: RequestTypeEnum): Promise<any> {
        return (this.http.post<T>(url, data, this.prepareOptions(options, requestType)).toPromise());
    }

    protected put<T>(url: string, data?: any, options?: any, requestType?: RequestTypeEnum): Promise<any> {
        return (this.http.put<T>(url, data, this.prepareOptions(options, requestType)).toPromise());
    }

    protected patch<T>(url: string, data?: any, options?: any, requestType?: RequestTypeEnum): Promise<any> {
        return (this.http.patch<T>(url, data, this.prepareOptions(options, requestType)).toPromise());
    }

    protected delete<T>(url: string, options?: any, requestType?: RequestTypeEnum): Promise<any> {
        return (this.http.delete<T>(url, this.prepareOptions(options, requestType)).toPromise());
    }

    protected toQueryParams(model: any): string {
        const params = Object.keys(model).filter(k => !!model[k])
            .map(k => {
                if (typeof model[k] !== 'string' && !!model[k].length) {
                    return model[k].map(i => `${encodeURIComponent(k)}=${encodeURIComponent(i)}`).join(`&`);
                }

                return `${encodeURIComponent(k)}=${encodeURIComponent(model[k])}`;
            }).join('&');
        return params;
    }

    /**
    * Recursively sanitize all string properties in an object using DOMPurify.
    * @param obj The object to sanitize.
    * @returns A new object with sanitized string properties.
    */
    protected sanitizeObject<T>(obj: T): T {
        if (obj === null || obj === undefined) return obj;

        if (typeof obj === 'string') {
            return sanitizeHtml(obj) as unknown as T;
        }

        if (Array.isArray(obj)) {
            return obj.map(item => this.sanitizeObject(item)) as unknown as T;
        }

        if (typeof obj === 'object') {
            const sanitized: any = {};
            for (const key of Object.keys(obj)) {
                sanitized[key] = this.sanitizeObject((obj as any)[key]);
            }
            return sanitized;
        }

        return obj;
    }
}
